import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import {
  TenantsAPI, InstitutionsAPI, YearsAPI, ClassesAPI, SectionsAPI, SubjectsAPI,
} from "@/lib/sprint2-api";
import { StudentsAPI } from "@/lib/sprint3-api";
import { ExamTypesAPI, ExamsAPI, StudentSummaryAPI } from "@/lib/gradebook-api";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, FileText, Award, ClipboardEdit, GraduationCap, ChevronLeft } from "lucide-react";

export default function Gradebook() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const queryClient = useQueryClient();

  const [tenantId, setTenantId] = useState("");
  const [institutionId, setInstitutionId] = useState("");
  const [yearId, setYearId] = useState("");
  const [classId, setClassId] = useState("");
  const [openExamId, setOpenExamId] = useState(null);

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });
  const effectiveTenantId = isSuper
    ? (tenantId || tenantsQuery.data?.[0]?.id || "")
    : (user?.tenant_id || "");
  const tenantParam = isSuper && effectiveTenantId ? { tenant_id: effectiveTenantId } : undefined;

  const institutionsQuery = useQuery({
    queryKey: ["institutions", effectiveTenantId],
    queryFn: () => InstitutionsAPI.list(tenantParam),
    enabled: !!effectiveTenantId,
  });
  const effectiveInstitutionId =
    (institutionsQuery.data?.some((i) => i.id === institutionId) ? institutionId : "") ||
    institutionsQuery.data?.[0]?.id ||
    "";

  const yearsQuery = useQuery({
    queryKey: ["years", effectiveInstitutionId],
    queryFn: () => YearsAPI.list({ institution_id: effectiveInstitutionId, ...(tenantParam || {}) }),
    enabled: !!effectiveInstitutionId,
  });
  const effectiveYearId = (() => {
    const list = yearsQuery.data || [];
    if (list.some((y) => y.id === yearId)) return yearId;
    return list.find((y) => y.is_current)?.id || list[0]?.id || "";
  })();

  const classesQuery = useQuery({
    queryKey: ["classes", effectiveYearId],
    queryFn: () => ClassesAPI.list({ academic_year_id: effectiveYearId, ...(tenantParam || {}) }),
    enabled: !!effectiveYearId,
  });
  const effectiveClassId =
    (classesQuery.data?.some((c) => c.id === classId) ? classId : "") ||
    classesQuery.data?.[0]?.id ||
    "";

  const invalidateExams = () => queryClient.invalidateQueries({ queryKey: ["gb-exams"] });

  return (
    <AppShell>
      <div className="p-8" data-testid="gradebook-page">
        <div className="mb-6">
          <h1 className="font-display font-extrabold tracking-tight text-4xl">Gradebook</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Configure exam types, schedule exams per class &amp; subject, bulk-enter marks and view per-student grade summaries against the institution&apos;s default scale.
          </p>
        </div>

        {/* Scope */}
        <div className="flex flex-wrap gap-3 mb-6 p-4 border border-border rounded-md bg-card">
          {isSuper && (
            <Scope
              label="Tenant"
              value={effectiveTenantId}
              onChange={(v) => { setTenantId(v); setInstitutionId(""); setYearId(""); setClassId(""); }}
              options={tenantsQuery.data || []}
              testid="gb-scope-tenant"
            />
          )}
          <Scope
            label="Institution"
            value={effectiveInstitutionId}
            onChange={(v) => { setInstitutionId(v); setYearId(""); setClassId(""); }}
            options={institutionsQuery.data || []}
            testid="gb-scope-institution"
          />
          <Scope
            label="Academic year"
            value={effectiveYearId}
            onChange={(v) => { setYearId(v); setClassId(""); }}
            options={(yearsQuery.data || []).map((y) => ({ id: y.id, name: y.name + (y.is_current ? " · current" : "") }))}
            disabled={!effectiveInstitutionId}
            testid="gb-scope-year"
          />
          <Scope
            label="Class"
            value={effectiveClassId}
            onChange={setClassId}
            options={classesQuery.data || []}
            disabled={!effectiveYearId}
            testid="gb-scope-class"
          />
        </div>

        {openExamId ? (
          <MarksEntry
            examId={openExamId}
            tenantId={effectiveTenantId}
            onClose={() => { setOpenExamId(null); invalidateExams(); }}
          />
        ) : (
          <Tabs defaultValue="exams">
            <TabsList>
              <TabsTrigger value="exams" data-testid="gb-tab-exams"><ClipboardEdit className="h-3.5 w-3.5 mr-1.5" />Exams</TabsTrigger>
              <TabsTrigger value="types" data-testid="gb-tab-types"><FileText className="h-3.5 w-3.5 mr-1.5" />Exam Types</TabsTrigger>
              <TabsTrigger value="summary" data-testid="gb-tab-summary"><Award className="h-3.5 w-3.5 mr-1.5" />Student Summary</TabsTrigger>
            </TabsList>
            <TabsContent value="exams" className="mt-6">
              <ExamsPanel
                tenantId={effectiveTenantId}
                institutionId={effectiveInstitutionId}
                classId={effectiveClassId}
                onOpenExam={setOpenExamId}
              />
            </TabsContent>
            <TabsContent value="types" className="mt-6">
              <ExamTypesPanel tenantId={effectiveTenantId} institutionId={effectiveInstitutionId} />
            </TabsContent>
            <TabsContent value="summary" className="mt-6">
              <StudentSummaryPanel
                tenantId={effectiveTenantId}
                institutionId={effectiveInstitutionId}
                classId={effectiveClassId}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </AppShell>
  );
}

function Scope({ label, value, onChange, options, disabled, testid }) {
  return (
    <div className="space-y-1.5 min-w-[180px]">
      <Label className="text-[10px] font-semibold uppercase tracking-wider">{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled || !options.length}>
        <SelectTrigger className="w-full" data-testid={testid}>
          <SelectValue placeholder={disabled ? "—" : `Select ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------------- Exam Types ----------------
function ExamTypesPanel({ tenantId, institutionId }) {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["gb-exam-types", tenantId || "self", institutionId],
    queryFn: () => ExamTypesAPI.list(institutionId, tenantId),
    enabled: !!institutionId,
  });
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const add = async () => {
    if (!name.trim()) return toast.error("Name required");
    try {
      await ExamTypesAPI.create({ institution_id: institutionId, name: name.trim(), code: code.trim() || null }, tenantId);
      setName(""); setCode("");
      queryClient.invalidateQueries({ queryKey: ["gb-exam-types"] });
      toast.success("Exam type added");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await ExamTypesAPI.remove(id, tenantId); queryClient.invalidateQueries({ queryKey: ["gb-exam-types"] }); toast.success("Removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  if (!institutionId) return <Empty msg="Pick an institution to manage exam types." />;
  return (
    <div className="space-y-3">
      <div className="border border-border rounded-md bg-card p-4 flex gap-2 flex-wrap items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs font-semibold uppercase tracking-wider">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Unit Test, Mid-term, Final…" data-testid="gb-type-name" />
        </div>
        <div className="w-32">
          <Label className="text-xs font-semibold uppercase tracking-wider">Code</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} className="font-mono" placeholder="UT1" data-testid="gb-type-code" />
        </div>
        <Button onClick={add} data-testid="gb-type-add"><Plus className="h-4 w-4 mr-1.5" />Add</Button>
      </div>
      <div className="border border-border rounded-md bg-card">
        {q.isLoading ? <Loading /> : (q.data?.length ? (
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Description</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {q.data.map((t) => (
                <TableRow key={t.id} data-testid={`gb-type-row-${t.id}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="font-mono text-xs">{t.code || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.description || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => remove(t.id)} data-testid={`gb-type-del-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <Empty msg="No exam types yet. Add Unit Test / Mid-term / Final to start." />)}
      </div>
    </div>
  );
}

// ---------------- Exams ----------------
function ExamsPanel({ tenantId, institutionId, classId, onOpenExam }) {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["gb-exams", tenantId || "self", classId],
    queryFn: () => ExamsAPI.list({ class_id: classId }, tenantId),
    enabled: !!classId,
  });
  const subjectsQuery = useQuery({
    queryKey: ["gb-subjects", classId],
    queryFn: () => SubjectsAPI.list({ class_id: classId, ...(tenantId ? { tenant_id: tenantId } : {}) }),
    enabled: !!classId,
  });
  const sectionsQuery = useQuery({
    queryKey: ["gb-sections", classId],
    queryFn: () => SectionsAPI.list({ class_id: classId, ...(tenantId ? { tenant_id: tenantId } : {}) }),
    enabled: !!classId,
  });
  const typesQuery = useQuery({
    queryKey: ["gb-exam-types", tenantId || "self", institutionId],
    queryFn: () => ExamTypesAPI.list(institutionId, tenantId),
    enabled: !!institutionId,
  });

  if (!classId) return <Empty msg="Pick a class to schedule exams." />;

  const remove = async (id) => {
    if (!window.confirm("Delete this exam and all its marks?")) return;
    try { await ExamsAPI.remove(id, tenantId); queryClient.invalidateQueries({ queryKey: ["gb-exams"] }); toast.success("Exam removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <CreateExamDialog
          tenantId={tenantId}
          institutionId={institutionId}
          classId={classId}
          subjects={subjectsQuery.data || []}
          sections={sectionsQuery.data || []}
          examTypes={typesQuery.data || []}
          onDone={() => queryClient.invalidateQueries({ queryKey: ["gb-exams"] })}
        />
      </div>
      <div className="border border-border rounded-md bg-card">
        {q.isLoading ? <Loading /> : (q.data?.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Section</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Max</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.data.map((e) => {
                const sub = (subjectsQuery.data || []).find((s) => s.id === e.subject_id);
                const sec = (sectionsQuery.data || []).find((s) => s.id === e.section_id);
                const t = (typesQuery.data || []).find((x) => x.id === e.exam_type_id);
                return (
                  <TableRow key={e.id} data-testid={`gb-exam-row-${e.id}`}>
                    <TableCell className="font-medium">{e.title}</TableCell>
                    <TableCell>{sub?.name || "—"}</TableCell>
                    <TableCell>{sec?.name || "All"}</TableCell>
                    <TableCell>{t?.name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{e.exam_date}</TableCell>
                    <TableCell>{e.max_marks}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={e.status === "graded" ? "bg-[hsl(var(--success))/0.1] text-[hsl(var(--success))] border-[hsl(var(--success))/0.2]" : "text-muted-foreground"}>
                        {e.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="outline" onClick={() => onOpenExam(e.id)} data-testid={`gb-exam-marks-${e.id}`}>
                        <ClipboardEdit className="h-3.5 w-3.5 mr-1" />Marks
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(e.id)} data-testid={`gb-exam-del-${e.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : <Empty msg="No exams yet. Create one to start grading." />)}
      </div>
    </div>
  );
}

function CreateExamDialog({ tenantId, institutionId, classId, subjects, sections, examTypes, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", subject_id: "", section_id: "", exam_type_id: "", exam_date: "", max_marks: 100 });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await ExamsAPI.create({
        institution_id: institutionId,
        class_id: classId,
        section_id: form.section_id || null,
        subject_id: form.subject_id,
        exam_type_id: form.exam_type_id,
        title: form.title,
        exam_date: form.exam_date,
        max_marks: Number(form.max_marks) || 100,
      }, tenantId);
      toast.success("Exam created");
      setForm({ title: "", subject_id: "", section_id: "", exam_type_id: "", exam_date: "", max_marks: 100 });
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disabled = !subjects.length || !examTypes.length;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled} data-testid="gb-create-exam"><Plus className="h-4 w-4 mr-1.5" />Exam</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New exam</DialogTitle></DialogHeader>
        {disabled && <div className="text-xs text-muted-foreground border border-border rounded-md p-2 mb-2">Add at least one subject (Academic Setup) and one exam type before creating an exam.</div>}
        <form onSubmit={submit} className="space-y-3">
          <Field label="Title">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="gb-exam-title" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Subject">
              <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                <SelectTrigger data-testid="gb-exam-subject"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Exam Type">
              <Select value={form.exam_type_id} onValueChange={(v) => setForm({ ...form, exam_type_id: v })}>
                <SelectTrigger data-testid="gb-exam-type"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{examTypes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Section (optional)">
              <Select value={form.section_id} onValueChange={(v) => setForm({ ...form, section_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="gb-exam-section"><SelectValue placeholder="All sections" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sections</SelectItem>
                  {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date">
              <Input type="date" value={form.exam_date} onChange={(e) => setForm({ ...form, exam_date: e.target.value })} required data-testid="gb-exam-date" />
            </Field>
            <Field label="Max marks">
              <Input type="number" min="1" value={form.max_marks} onChange={(e) => setForm({ ...form, max_marks: e.target.value })} required data-testid="gb-exam-max" />
            </Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !form.subject_id || !form.exam_type_id} data-testid="gb-exam-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Marks entry ----------------
function MarksEntry({ examId, tenantId, onClose }) {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ["gb-exam-marks", tenantId || "self", examId],
    queryFn: () => ExamsAPI.getMarks(examId, tenantId),
  });
  const [draft, setDraft] = useState({}); // student_id -> {marks, absent, remark}
  const [busy, setBusy] = useState(false);

  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <Empty msg="Couldn't load exam roster." />;
  const { exam, roster, can_grade } = q.data;
  const merged = roster.map((r) => ({ ...r, ...(draft[r.student_id] || {}) }));

  const update = (sid, patch) =>
    setDraft((d) => ({ ...d, [sid]: { ...(d[sid] || {}), ...patch } }));

  const save = async () => {
    const entries = Object.keys(draft).map((sid) => ({
      student_id: sid,
      marks: draft[sid].marks === undefined ? merged.find((r) => r.student_id === sid)?.marks ?? null : (draft[sid].marks === "" ? null : Number(draft[sid].marks)),
      absent: draft[sid].absent ?? merged.find((r) => r.student_id === sid)?.absent ?? false,
      remark: draft[sid].remark ?? merged.find((r) => r.student_id === sid)?.remark ?? null,
    }));
    if (!entries.length) { toast.message("No changes to save"); return; }
    setBusy(true);
    try {
      const out = await ExamsAPI.saveMarks(examId, entries, tenantId);
      toast.success(`Saved ${out.saved} mark${out.saved === 1 ? "" : "s"}`);
      setDraft({});
      queryClient.invalidateQueries({ queryKey: ["gb-exam-marks"] });
      queryClient.invalidateQueries({ queryKey: ["gb-exams"] });
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="gb-marks-entry">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="gb-marks-back">
            <ChevronLeft className="h-4 w-4 mr-1" /> Exams
          </Button>
          <div>
            <h2 className="font-display font-bold text-xl">{exam.title}</h2>
            <div className="text-xs text-muted-foreground font-mono">
              {exam.exam_date} · max {exam.max_marks} · {exam.status}
            </div>
          </div>
        </div>
        <Button onClick={save} disabled={busy || !can_grade || !Object.keys(draft).length} data-testid="gb-marks-save">
          {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save {Object.keys(draft).length || ""} mark{Object.keys(draft).length === 1 ? "" : "s"}
        </Button>
      </div>
      {!can_grade && <div className="text-xs text-amber-700 border border-amber-300 bg-amber-50 rounded-md px-3 py-2">Read-only — you don&apos;t have permission to grade this exam.</div>}
      <div className="border border-border rounded-md bg-card">
        {merged.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Admission</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-32">Marks</TableHead>
                <TableHead className="w-24">Absent</TableHead>
                <TableHead>Remark</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {merged.map((r) => (
                <TableRow key={r.student_id} data-testid={`gb-mark-row-${r.admission_no}`}>
                  <TableCell className="font-mono text-xs">{r.admission_no}</TableCell>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      max={exam.max_marks}
                      step="0.25"
                      value={r.marks ?? ""}
                      disabled={!can_grade || r.absent}
                      onChange={(e) => update(r.student_id, { marks: e.target.value })}
                      className="font-mono"
                      data-testid={`gb-mark-input-${r.admission_no}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!r.absent}
                      disabled={!can_grade}
                      onCheckedChange={(v) => update(r.student_id, { absent: v, marks: v ? null : r.marks })}
                      data-testid={`gb-mark-absent-${r.admission_no}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={r.remark || ""}
                      disabled={!can_grade}
                      onChange={(e) => update(r.student_id, { remark: e.target.value })}
                      placeholder="optional"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <Empty msg="No students in this class/section yet." />}
      </div>
    </div>
  );
}

// ---------------- Student Summary ----------------
function StudentSummaryPanel({ tenantId, institutionId, classId }) {
  const [studentId, setStudentId] = useState("");
  const studentsQuery = useQuery({
    queryKey: ["gb-students", tenantId || "self", classId],
    queryFn: () => StudentsAPI.list({ institution_id: institutionId, class_id: classId, ...(tenantId ? { tenant_id: tenantId } : {}) }),
    enabled: !!classId && !!institutionId,
  });
  const summaryQuery = useQuery({
    queryKey: ["gb-summary", tenantId || "self", studentId],
    queryFn: () => StudentSummaryAPI.get(studentId, tenantId),
    enabled: !!studentId,
  });

  if (!classId) return <Empty msg="Pick a class to view student summaries." />;
  return (
    <div className="space-y-4">
      <div className="border border-border rounded-md bg-card p-4 flex gap-3 items-end flex-wrap">
        <div className="min-w-[260px] flex-1">
          <Label className="text-xs font-semibold uppercase tracking-wider">Student</Label>
          <Select value={studentId} onValueChange={setStudentId} disabled={!studentsQuery.data?.length}>
            <SelectTrigger data-testid="gb-summary-student">
              <SelectValue placeholder={studentsQuery.data?.length ? "Pick a student" : "No students in this class"} />
            </SelectTrigger>
            <SelectContent>
              {(studentsQuery.data || []).map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.admission_no} · {s.first_name} {s.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {studentId && (summaryQuery.isLoading ? <Loading /> : (summaryQuery.data ? <SummaryCard data={summaryQuery.data} /> : <Empty msg="No summary." />))}
    </div>
  );
}

function SummaryCard({ data }) {
  const { student, scale_name, rows, totals } = data;
  return (
    <div className="border border-border rounded-md bg-card" data-testid="gb-summary-card">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-5 w-5 text-primary" />
          <div>
            <div className="font-display font-bold text-lg">{student.name}</div>
            <div className="text-xs text-muted-foreground font-mono">Admission {student.admission_no}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Overall</div>
          <div className="font-display font-extrabold text-3xl tracking-tight">
            {totals.percentage !== null ? `${totals.percentage}%` : "—"}
            {totals.grade && <span className="ml-2 text-base text-primary">{totals.grade.label}</span>}
          </div>
          <div className="text-xs text-muted-foreground">{totals.marks_obtained}/{totals.marks_total} across {totals.exams_graded} exam{totals.exams_graded === 1 ? "" : "s"}</div>
          {scale_name && <div className="text-[10px] text-muted-foreground mt-0.5">Scale · {scale_name}</div>}
        </div>
      </div>
      {rows.length ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Exam</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Marks</TableHead>
              <TableHead className="text-right">%</TableHead>
              <TableHead className="text-right">Grade</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.exam_id} data-testid={`gb-summary-row-${r.exam_id}`}>
                <TableCell className="font-medium">{r.title}</TableCell>
                <TableCell>{r.subject || "—"}</TableCell>
                <TableCell>{r.exam_type || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.exam_date}</TableCell>
                <TableCell className="text-right font-mono">
                  {r.absent ? <span className="text-destructive">Absent</span> :
                    r.marks === null ? <span className="text-muted-foreground">—</span> :
                    `${r.marks}/${r.max_marks}`}
                </TableCell>
                <TableCell className="text-right font-mono">{r.percentage !== null ? `${r.percentage}%` : "—"}</TableCell>
                <TableCell className="text-right">
                  {r.grade ? (
                    <Badge variant="outline" className={r.grade.is_pass ? "bg-primary/10 text-primary border-primary/20" : "bg-destructive/10 text-destructive border-destructive/20"}>
                      {r.grade.label}
                    </Badge>
                  ) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : <Empty msg="No exams scheduled for this student yet." />}
    </div>
  );
}

function Empty({ msg }) {
  return <div className="p-10 text-center text-sm text-muted-foreground border border-border rounded-md bg-card">{msg}</div>;
}
function Loading() {
  return <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline" /></div>;
}
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}
